import { useEffect, useMemo, useRef, useState } from 'react';
import { hasBlocks } from '../labEngine';
import { computeCBR, suggestOriginShift, PENS, REQUIRED_PENS } from './cbrCalc';
import { addDays, cbrContainerG, mouldG } from '../labMaster';
import './CBRTest.css';

const n = (s) => (s === '' || s == null ? NaN : Number(s));
const blankMould = () => ({
  mouldNo: '', blows: '', mouldMass: '', volume: '2250', mouldSoil: '', contNo: '', wet: '', dry: '', tare: '',
  mouldSoilA: '', contNoA: '', wetA: '', dryA: '', tareA: '',
  ds: '', dr: '', height: '127.3', shift: '', dial: PENS.map(() => ''),
});
const ROMAN = ['I', 'II', 'III'];

// effective values: a typed weight wins, otherwise it is looked up from the number (labMaster.js)
const eff = {
  mouldMass: (m) => (m.mouldMass !== '' ? n(m.mouldMass) : (mouldG(m.mouldNo) ?? NaN)),
  tare: (m) => (m.tare !== '' ? n(m.tare) : (cbrContainerG(m.contNo) ?? NaN)),
  tareA: (m) => (m.tareA !== '' ? n(m.tareA) : (cbrContainerG(m.contNoA) ?? NaN)),
};
const REQ = [ // always typed: [key, label, step]
  ['blows', 'Blows per layer'], ['volume', 'Mould volume (cc)'], ['mouldSoil', 'Mould + soil (g)'],
  ['wet', 'Container + wet (g)'], ['dry', 'Container + dry (g)'],
  ['ds', 'Swell dial, start (mm)'], ['dr', 'Swell dial, end (mm)'], ['height', 'Specimen height (mm)'],
];
const AFTER_KEYS = ['mouldSoilA', 'contNoA', 'wetA', 'dryA', 'tareA'];
const afterStarted = (m) => AFTER_KEYS.some((k) => m[k] !== '');
const mouldStarted = (m) => m.dial.some((d) => d !== '') || Object.entries(m).some(([k, v]) => k !== 'dial' && !['volume', 'height', 'shift'].includes(k) && v !== '');
const mouldMissing = (m) => [
  ...REQ.filter(([k]) => !Number.isFinite(n(m[k]))).map(([, l]) => l),
  ...(Number.isFinite(eff.mouldMass(m)) ? [] : ['Mould weight (or mould no.)']),
  ...(Number.isFinite(eff.tare(m)) ? [] : ['Container (g) (or container no.)']),
  ...(m.dial.slice(0, REQUIRED_PENS).some((d) => !Number.isFinite(n(d))) ? [`dial readings 0–${PENS[REQUIRED_PENS - 1]} mm`] : []),
  ...(afterStarted(m) ? [
    ...(Number.isFinite(n(m.mouldSoilA)) ? [] : ['After soaking: mould + soil']),
    ...(Number.isFinite(n(m.wetA)) ? [] : ['After soaking: container + wet']),
    ...(Number.isFinite(n(m.dryA)) ? [] : ['After soaking: container + dry']),
    ...(Number.isFinite(eff.tareA(m)) ? [] : ['After soaking: container (or container no.)']),
  ] : []),
];
const readingsOf = (m) => PENS.map((pen, p) => ({ pen, dial: n(m.dial[p]) })).filter((r) => Number.isFinite(r.dial));

// California bearing ratio — IS 2720 (Part 16):1987. Up to three moulds (e.g. 10 / 30 / 65 blows), soaked.
// Auto: mould / container weights from their numbers, MDD + OMC from the MDD test, dates from the header, origin shift from the curve.
export default function CBRTest({ header = {}, others, onReport }) {
  const [setup, setSetup] = useState({
    ring: '', area: '19.64', soak: '96', surcharge: '5', compaction: '', oversize: '', mdd: '', omc: '',
    source: '', proposedUse: '', castingOn: '', testedOn: '',
  });
  const [moulds, setMoulds] = useState(() => [blankMould(), blankMould(), blankMould()]);
  const setS = (k) => (e) => setSetup((s) => ({ ...s, [k]: e.target.value }));
  const setM = (i, k) => (e) => setMoulds((ms) => ms.map((m, j) => (j === i ? { ...m, [k]: e.target.value } : m)));
  const setDial = (i, p) => (e) => setMoulds((ms) => ms.map((m, j) => (j === i ? { ...m, dial: m.dial.map((d, q) => (q === p ? e.target.value : d)) } : m)));

  // ---- values that fill themselves (all can be overwritten)
  const mddRes = others?.mdd?.payload?.result;
  const mddAuto = mddRes && Number.isFinite(mddRes.mdd) ? mddRes.mdd : null;
  const omcAuto = mddRes && Number.isFinite(mddRes.omc) ? mddRes.omc : null;
  const mddEff = setup.mdd !== '' ? n(setup.mdd) : mddAuto;
  const omcEff = setup.omc !== '' ? n(setup.omc) : omcAuto;
  const castingEff = setup.castingOn || addDays(header.testedOn, 1);                         // cast the day after the compaction test
  const testedEff = setup.testedOn || addDays(castingEff, Math.round((n(setup.soak) || 96) / 24)); // tested after the soak
  const ringN = n(setup.ring), areaN = n(setup.area);
  const shiftAuto = (m) => suggestOriginShift(readingsOf(m), ringN, areaN);
  const shiftOf = (m) => (m.shift !== '' ? n(m.shift) : shiftAuto(m));

  const active = moulds.map((m, i) => ({ m, i })).filter(({ m }) => mouldStarted(m));
  const missing = [
    ...[['proving ring factor', setup.ring], ['plunger area', setup.area], ['soaking time', setup.soak], ['surcharge weight', setup.surcharge], ['oversize replaced (kg)', setup.oversize]]
      .filter(([, v]) => !Number.isFinite(n(v))).map(([l]) => l),
    ...(setup.compaction ? [] : ['compaction (light/heavy)']),
    ...(active.length ? [] : ['mould I readings']),
    ...active.flatMap(({ m, i }) => mouldMissing(m).map((l) => `mould ${ROMAN[i]} ${l}`)),
  ];
  const canCompute = missing.length === 0;

  const inputs = useMemo(() => canCompute ? {
    ringFactor: ringN, areaCm2: areaN, soakHours: n(setup.soak), surchargeKg: n(setup.surcharge), compaction: setup.compaction,
    replacedOversizeKg: n(setup.oversize), labMddGcc: Number.isFinite(mddEff) ? mddEff : null, omcPct: Number.isFinite(omcEff) ? omcEff : null,
    castingOn: castingEff, testedOn: testedEff, source: (setup.source.trim() || header.source || '').trim(), proposedUse: setup.proposedUse.trim(),
    moulds: active.map(({ m }) => ({
      mouldNo: m.mouldNo === '' ? null : Number(m.mouldNo),
      blows: n(m.blows), mouldMassG: eff.mouldMass(m), volumeCc: n(m.volume), mouldSoilG: n(m.mouldSoil),
      contNo: m.contNo === '' ? null : Number(m.contNo), wetG: n(m.wet), dryG: n(m.dry), tareG: eff.tare(m),
      after: afterStarted(m) ? { mouldSoilG: n(m.mouldSoilA), contNo: m.contNoA === '' ? null : Number(m.contNoA), wetG: n(m.wetA), dryG: n(m.dryA), tareG: eff.tareA(m) } : null,
      dsMm: n(m.ds), drMm: n(m.dr), heightMm: n(m.height), originShiftMm: shiftOf(m),
      readings: readingsOf(m),
    })),
  } : null, [canCompute, setup, moulds, mddEff, omcEff, castingEff, testedEff]); // eslint-disable-line react-hooks/exhaustive-deps

  const { result, issues } = useMemo(() => (inputs ? computeCBR(inputs) : { result: null, issues: [] }), [inputs]);
  const ready = !!result && !hasBlocks(issues);
  const dirty = active.length > 0 || ['ring', 'oversize', 'compaction', 'mdd'].some((k) => setup[k] !== '');
  const hint = ready ? '' : !dirty ? 'enter the readings' : missing.length ? `fill ${missing[0]}${missing.length > 1 ? ` (+${missing.length - 1} more)` : ''}`
    : (issues.find((i) => i.level === 'block')?.msg || '');

  const report = useMemo(() => ({
    status: ready ? 'ready' : dirty ? 'incomplete' : 'empty', ready, dirty, hint,
    summary: ready ? `${result.reportedLabel} ${result.reportedCbr.toFixed(1)} % · Swell ${result.maxExpansion.toFixed(2)} %` : '',
    payload: ready ? { inputs, result, issues } : null,
  }), [ready, dirty, hint, result, issues, inputs]);
  const cb = useRef(onReport); cb.current = onReport;
  useEffect(() => { cb.current?.(report); }, [report]);

  // live per-mould numbers while typing (shown even before the whole form is complete)
  const live = (m) => {
    const mass = eff.mouldMass(m), vol = n(m.volume), soil = n(m.mouldSoil), wet = n(m.wet), dry = n(m.dry), tare = eff.tare(m);
    const out = {};
    if (mass > 0 && vol > 0 && soil > mass && dry > tare && wet >= dry) {
      const w = ((wet - dry) / (dry - tare)) * 100, bulk = (soil - mass) / vol;
      out.w = w; out.dry = (100 * bulk) / (100 + w);
      if (Number.isFinite(mddEff)) out.comp = (out.dry / mddEff) * 100;
      if (Number.isFinite(omcEff)) out.omcVar = w - omcEff;
    }
    const sA = n(m.mouldSoilA), wA = n(m.wetA), dA = n(m.dryA), tA = eff.tareA(m);
    if (mass > 0 && vol > 0 && sA > mass && dA > tA && wA >= dA) {
      const w = ((wA - dA) / (dA - tA)) * 100, bulk = (sA - mass) / vol;
      out.wA = w; out.dryA = (100 * bulk) / (100 + w);
    }
    return out;
  };

  const inp = (value, onChange, label, step = '0.01', placeholder = '') => (
    <input className="cbr-input" type="number" step={step} min="0" inputMode="decimal" aria-label={label} value={value} onChange={onChange} placeholder={placeholder} />
  );
  const dateInp = (value, onChange, label) => <input className="cbr-input" type="date" aria-label={label} value={value} onChange={onChange} />;
  const row = (label, key, step, ph) => (
    <div key={key} className="cbr-grid cbr-row">
      <span className="cbr-lab">{label}</span>
      {moulds.map((m, i) => <span key={i}>{inp(m[key], setM(i, key), `Mould ${ROMAN[i]} ${label}`, step, ph ? ph(m) : '')}</span>)}
    </div>
  );
  const calcRow = (label, fn, fmt, bold) => (
    <div className="cbr-grid cbr-row">
      <span className="cbr-lab">{bold ? <b>{label}</b> : label}</span>
      {moulds.map((m, i) => { const v = fn(live(m), m); return <span key={i} className="cbr-calc">{Number.isFinite(v) ? v.toFixed(fmt) : '—'}</span>; })}
    </div>
  );
  const lookupNote = (no, table) => (no === '' ? '' : table(no) != null ? `${table(no)} g` : 'not in list');

  return (
    <div className="cbr">
      <section>
        <div className="cbr-sec-head"><h4>Test set-up</h4><span className="cbr-muted">soaked CBR · {setup.soak || 96} h · dynamic compaction</span></div>
        <div className="cbr-three">
          <label className="cbr-field">Proving ring factor (kg / div) *{inp(setup.ring, setS('ring'), 'Ring factor', '0.001')}</label>
          <label className="cbr-field">Plunger area (cm²) *{inp(setup.area, setS('area'), 'Plunger area', '0.01')}</label>
          <label className="cbr-field">Surcharge weight (kg) *{inp(setup.surcharge, setS('surcharge'), 'Surcharge weight', '0.1')}</label>
          <label className="cbr-field">Soaking time (h) *{inp(setup.soak, setS('soak'), 'Soaking time', '1')}</label>
          <label className="cbr-field">Compaction *
            <select className="cbr-input" value={setup.compaction} onChange={setS('compaction')}>
              <option value="">—</option><option value="light">Light</option><option value="heavy">Heavy</option></select></label>
          <label className="cbr-field">Oversize replaced (kg) *{inp(setup.oversize, setS('oversize'), 'Oversize replaced', '0.01')}
            <span className="cbr-muted">enter 0 if none</span></label>
          <label className="cbr-field">Lab MDD (g/cc)
            {inp(setup.mdd, setS('mdd'), 'Lab MDD', '0.001', mddAuto != null ? mddAuto.toFixed(2) : '')}
            <span className="cbr-muted">{setup.mdd !== '' ? 'typed value used' : mddAuto != null ? 'taken from the MDD test' : 'gives CBR at 95 / 97 / 100 % of MDD'}</span></label>
          <label className="cbr-field">OMC (%)
            {inp(setup.omc, setS('omc'), 'OMC', '0.1', omcAuto != null ? omcAuto.toFixed(1) : '')}
            <span className="cbr-muted">{setup.omc !== '' ? 'typed value used' : omcAuto != null ? 'taken from the MDD test' : 'used for the OMC-variation check'}</span></label>
          <label className="cbr-field">Date of casting{dateInp(castingEff, setS('castingOn'), 'Date of casting')}
            <span className="cbr-muted">{setup.castingOn ? 'typed' : 'auto: day after the compaction test'}</span></label>
          <label className="cbr-field">Date of testing{dateInp(testedEff, setS('testedOn'), 'Date of testing')}
            <span className="cbr-muted">{setup.testedOn ? 'typed' : 'auto: casting + soaking days'}</span></label>
          <label className="cbr-field">Source <span className="cbr-opt">optional</span>
            <input className="cbr-input" value={setup.source} onChange={setS('source')} placeholder={header.source || 'e.g. Existing soil'} /></label>
          <label className="cbr-field">Proposed use <span className="cbr-opt">optional</span>
            <input className="cbr-input" value={setup.proposedUse} onChange={setS('proposedUse')} placeholder="e.g. Subgrade" /></label>
        </div>
      </section>

      <section>
        <div className="cbr-sec-head"><h4>Moulds — before soaking</h4><span className="cbr-muted">mould I is required; II and III give CBR vs density · mould / container no. fills the weight</span></div>
        <div className="cbr-scroll">
          <div className="cbr-grid cbr-labels"><span /><span>Mould I</span><span>Mould II</span><span>Mould III</span></div>
          <div className="cbr-grid cbr-row"><span className="cbr-lab">Mould no.</span>
            {moulds.map((m, i) => <span key={i}>{inp(m.mouldNo, setM(i, 'mouldNo'), `Mould ${ROMAN[i]} no.`, '1')}<small className="cbr-muted">{lookupNote(m.mouldNo, mouldG)}</small></span>)}</div>
          {row('Blows per layer *', 'blows', '1')}
          {row('Mould weight (g) *', 'mouldMass', '1', (m) => (mouldG(m.mouldNo) != null ? String(mouldG(m.mouldNo)) : ''))}
          {row('Mould volume (cc) *', 'volume', '1')}
          {row('Mould + soil (g) *', 'mouldSoil', '1')}
          <div className="cbr-grid cbr-row"><span className="cbr-lab">Container no.</span>
            {moulds.map((m, i) => <span key={i}>{inp(m.contNo, setM(i, 'contNo'), `Mould ${ROMAN[i]} container no.`, '1')}<small className="cbr-muted">{lookupNote(m.contNo, cbrContainerG)}</small></span>)}</div>
          {row('Container + wet (g) *', 'wet', '0.01')}
          {row('Container + dry (g) *', 'dry', '0.01')}
          {row('Container (g) *', 'tare', '0.01', (m) => (cbrContainerG(m.contNo) != null ? String(cbrContainerG(m.contNo)) : ''))}
          {calcRow('Moisture content (%)', (l) => l.w, 2)}
          {calcRow('Dry density (g/cc)', (l) => l.dry, 3, true)}
          {calcRow('Compaction (% of MDD)', (l) => l.comp, 1)}
          {calcRow('Moisture − OMC (%)', (l) => l.omcVar, 2)}
        </div>
      </section>

      <section>
        <div className="cbr-sec-head"><h4>Swell &amp; after soaking</h4><span className="cbr-muted">after-soaking weights are optional but printed in the Excel report</span></div>
        <div className="cbr-scroll">
          <div className="cbr-grid cbr-labels"><span /><span>Mould I</span><span>Mould II</span><span>Mould III</span></div>
          {row('Swell dial, start (mm) *', 'ds', '0.01')}
          {row('Swell dial, end (mm) *', 'dr', '0.01')}
          {row('Specimen height (mm) *', 'height', '0.1')}
          {row('Mould + soil after soaking (g)', 'mouldSoilA', '1')}
          <div className="cbr-grid cbr-row"><span className="cbr-lab">Container no. (after soaking)</span>
            {moulds.map((m, i) => <span key={i}>{inp(m.contNoA, setM(i, 'contNoA'), `Mould ${ROMAN[i]} container no. after soaking`, '1')}<small className="cbr-muted">{lookupNote(m.contNoA, cbrContainerG)}</small></span>)}</div>
          {row('Container + wet (g), after soaking', 'wetA', '0.01')}
          {row('Container + dry (g), after soaking', 'dryA', '0.01')}
          {row('Container (g), after soaking', 'tareA', '0.01', (m) => (cbrContainerG(m.contNoA) != null ? String(cbrContainerG(m.contNoA)) : ''))}
          {calcRow('Moisture after soaking (%)', (l) => l.wA, 2)}
          {calcRow('Dry density after soaking (g/cc)', (l) => l.dryA, 3)}
          <div className="cbr-grid cbr-row"><span className="cbr-lab">Origin shift (mm)<small className="cbr-muted"> auto from the §7.2 tangent; type to override</small></span>
            {moulds.map((m, i) => <span key={i}>{inp(m.shift, setM(i, 'shift'), `Mould ${ROMAN[i]} origin shift`, '0.01', Number.isFinite(ringN) && mouldStarted(m) ? shiftAuto(m).toFixed(2) : '')}</span>)}</div>
        </div>
      </section>

      <section>
        <div className="cbr-sec-head"><h4>Penetration — proving ring dial readings</h4><span className="cbr-muted">0 to 5 mm required; 7.5 – 12.5 mm optional</span></div>
        <div className="cbr-scroll">
          <div className="cbr-grid cbr-labels"><span>Penetration (mm)</span><span>Mould I</span><span>Mould II</span><span>Mould III</span></div>
          {PENS.map((pen, p) => (
            <div key={pen} className="cbr-grid cbr-row">
              <span className="cbr-lab">{pen.toFixed(1)}{p < REQUIRED_PENS ? ' *' : ''}</span>
              {moulds.map((m, i) => <span key={i}>{inp(m.dial[p], setDial(i, p), `Mould ${ROMAN[i]} dial at ${pen} mm`, '0.1')}</span>)}
            </div>))}
        </div>
      </section>

      {result && (
        <section className="cbr-result">
          <div className="cbr-stat"><span>{result.reportedLabel}</span><b>{result.reportedCbr.toFixed(1)}</b><small>%</small></div>
          <div className="cbr-stat"><span>Swell (max)</span><b>{result.maxExpansion.toFixed(2)}</b><small>% expansion ratio</small></div>
          {result.rows.map((r, i) => (
            <div key={i} className="cbr-stat"><span>Mould {ROMAN[i]}</span><b>{r.design.toFixed(1)}</b>
              <small>{r.cbr25.toFixed(1)} @ 2.5 mm · {r.cbr50.toFixed(1)} @ 5 mm</small></div>))}
          {result.atMdd && result.atMdd.map((a) => (
            <div key={a.level} className="cbr-stat"><span>At {a.level} % MDD</span><b>{a.cbr.toFixed(1)}</b><small>{a.extrapolated ? 'extrapolated' : '%'}</small></div>))}
        </section>
      )}
      {issues.length > 0 && (
        <ul className="cbr-issues">
          {issues.map((it, i) => (
            <li key={i} className={it.level === 'block' ? 'cbr-err' : 'cbr-warn'}>
              <b>{it.level === 'block' ? 'Fix' : 'Check'}</b> {it.msg} <span className="cbr-muted">({it.clause})</span></li>))}
        </ul>
      )}
      {dirty && missing.length > 0 && <p className="cbr-missing"><b>Still to fill:</b> {missing.slice(0, 8).join(', ')}{missing.length > 8 ? ` … +${missing.length - 8} more` : ''}</p>}
    </div>
  );
}
